/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailDraftSync.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEEMAILDRAFTSYNC-001
 * PURPOSE: Canonical body of this email hooks module. Lifted
 *          from apps/<tenant>/src/features/email/hooks/useEmailDraftSync.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/hooks/useEmailDraftSync.ts: thin re-export
 *     shim that points back here.
 */

import { useCallback, useEffect, useRef } from 'react'
import { emailApi } from '../lib/api'

export interface EmailDraftPayload {
  subject: string
  body: string
  body_html: string | null
  to: string[]
  cc: string[]
  bcc: string[]
  client_ref: string
  /** BUG-2026-07-17-EMAIL-DRAFT-403-WRONG-MAILBOX — the EmailAccount
   *  the compose is bound to (the From picker). When present the BE
   *  creates/updates the provider draft with THAT mailbox's Graph
   *  token instead of falling back to the user's primary Outlook
   *  account, which returned ErrorAccessDenied (403) whenever the
   *  compose was bound to a different (e.g. shared or other-org)
   *  mailbox. */
  email_account_id?: number | null
}

interface DraftCreateResponse {
  success: boolean
  draft_id: string | null
  /** 'gmail' | 'outlook' — set by the BE on every successful
   *  create / update so the FE can log / display which surface
   *  the draft actually landed on. The hook itself doesn't
   *  branch on it. */
  provider?: 'gmail' | 'outlook' | null
  web_link?: string | null
  client_ref?: string | null
  error?: string
}

interface UseEmailDraftSyncOptions {
  /** When true, sync is suppressed (e.g. operator hasn't connected
   *  either Gmail or Outlook yet). The hook still exposes its API
   *  but every call is a no-op. */
  disabled?: boolean
  /** Debounce window in ms. Defaults to 8000 (5s local autosave +
   *  3s "I think they've stopped typing" buffer). */
  debounceMs?: number
}

export interface UseEmailDraftSyncReturn {
  /** Schedule a server-side draft upsert. Debounced. Safe to call
   *  every keystroke / every local-autosave tick. No-op when the
   *  payload is empty (no subject, no body, no recipients). */
  scheduleSync: (payload: EmailDraftPayload | null) => void
  /** Cancel any pending upsert AND delete the provider draft (if
   *  any). Call on send-success and on explicit discard. */
  clear: () => Promise<void>
  /** Read the current provider-specific draft id for tests /
   *  debugging. Null until the first successful POST. */
  getDraftId: () => string | null
  /** Read the provider that owned the last successful upsert.
   *  Null until the first successful POST. */
  getProvider: () => 'gmail' | 'outlook' | null
}

export function draftPayloadForRequest(
  payload: EmailDraftPayload,
  accountId: number | null | undefined,
): EmailDraftPayload {
  const requestPayload = { ...payload }
  if (typeof accountId === 'number') {
    requestPayload.email_account_id = accountId
  } else {
    delete requestPayload.email_account_id
  }
  return requestPayload
}

export function useEmailDraftSync(
  options: UseEmailDraftSyncOptions = {},
): UseEmailDraftSyncReturn {
  const { disabled = false, debounceMs = 8_000 } = options
  const draftIdRef = useRef<string | null>(null)
  const providerRef = useRef<'gmail' | 'outlook' | null>(null)
  // The mailbox the provider draft was CREATED under. Id-routed
  // PATCH/DELETE calls must go out with the same account binding or
  // the BE resolves the user's primary mailbox and Graph rejects the
  // foreign draft id with 403/404.
  const accountIdRef = useRef<number | null>(null)
  const timerRef = useRef<number | null>(null)
  const inFlightRef = useRef<Promise<void> | null>(null)
  const lastPayloadRef = useRef<EmailDraftPayload | null>(null)

  const cancelPending = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const isEmpty = (p: EmailDraftPayload): boolean => {
    return !p.subject.trim()
      && !(p.body_html || '').replace(/<[^>]+>/g, '').trim()
      && p.to.length === 0
      && p.cc.length === 0
      && p.bcc.length === 0
  }

  const flush = useCallback(async () => {
    const payload = lastPayloadRef.current
    if (!payload || disabled) return
    if (isEmpty(payload)) return

    const id = draftIdRef.current
    // Wait for any in-flight request — back-pressure to avoid
    // racing POST + PATCH on the same draft.
    if (inFlightRef.current) {
      try { await inFlightRef.current } catch { /* swallow */ }
    }
    const promise = (async () => {
      try {
        if (id) {
          // Keep the update pinned to the mailbox that OWNS the draft —
          // never the (possibly re-picked) current payload account.
          const body = JSON.stringify(
            draftPayloadForRequest(
              payload,
              accountIdRef.current ?? payload.email_account_id,
            ),
          )
          const result = await emailApi<DraftCreateResponse>(
            `/api/emails/draft/${encodeURIComponent(id)}/`,
            { method: 'PATCH', body, headers: { 'Content-Type': 'application/json' } },
          )
          if (result?.provider) providerRef.current = result.provider
        } else {
          const body = JSON.stringify(
            draftPayloadForRequest(payload, payload.email_account_id),
          )
          const result = await emailApi<DraftCreateResponse>(
            '/api/emails/draft/',
            { method: 'POST', body, headers: { 'Content-Type': 'application/json' } },
          )
          if (result?.draft_id) {
            draftIdRef.current = result.draft_id
            accountIdRef.current =
              typeof payload.email_account_id === 'number'
                ? payload.email_account_id
                : null
          }
          if (result?.provider) providerRef.current = result.provider
        }
      } catch (err) {
        // Silent — local draft is the source of truth. Next save
        // will retry. Log only at info level (provider hiccups
        // are routine; they aren't user-visible failures).
        // eslint-disable-next-line no-console
        console.info('[email-drafts] sync failed (non-fatal):', err)
      } finally {
        inFlightRef.current = null
      }
    })()
    inFlightRef.current = promise
    await promise
  }, [disabled])

  const scheduleSync = useCallback((payload: EmailDraftPayload | null) => {
    if (disabled) return
    if (!payload) { cancelPending(); return }
    lastPayloadRef.current = payload
    cancelPending()
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void flush()
    }, debounceMs)
  }, [cancelPending, debounceMs, disabled, flush])

  const clear = useCallback(async () => {
    cancelPending()
    lastPayloadRef.current = null
    const id = draftIdRef.current
    if (!id) return
    const accountId = accountIdRef.current
    draftIdRef.current = null
    providerRef.current = null
    accountIdRef.current = null
    try {
      // DELETE has no body — carry the owning mailbox as a query param
      // so the BE routes the Graph delete with the right token.
      const qs = accountId != null
        ? `?email_account_id=${encodeURIComponent(String(accountId))}`
        : ''
      await emailApi(
        `/api/emails/draft/${encodeURIComponent(id)}/${qs}`,
        { method: 'DELETE' },
      )
    } catch (err) {
      // Same rationale as flush — silent on failure.
      // eslint-disable-next-line no-console
      console.info('[email-drafts] delete failed (non-fatal):', err)
    }
  }, [cancelPending])

  const getDraftId = useCallback(() => draftIdRef.current, [])
  const getProvider = useCallback(() => providerRef.current, [])

  // Cancel any pending timers on unmount. We don't clear() on
  // unmount because the most common unmount reason is closing the
  // dock without sending — we WANT the server-side draft to
  // persist so the operator can resume from Gmail / Outlook web
  // / mobile later.
  useEffect(() => () => cancelPending(), [cancelPending])

  return { scheduleSync, clear, getDraftId, getProvider }
}

// ---------------------------------------------------------------------------
// Back-compat alias — keep the old symbol importable for one MR so any
// in-flight branch that still references `useOutlookDraftSync` doesn't
// break. Will be removed in a follow-up cleanup.
// ---------------------------------------------------------------------------
export const useOutlookDraftSync = useEmailDraftSync
export type OutlookDraftPayload = EmailDraftPayload
export type UseOutlookDraftSyncReturn = UseEmailDraftSyncReturn
